import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowDown, ArrowUp, Eye, EyeOff, ImagePlus, Loader2, Pencil, Plus, Sticker, Trash2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ApiError, api, uploadFile } from '../lib/api';
import { chatKeys } from '../lib/chat';
import { useI18n } from '../lib/i18n';
import { formatNumber } from '../lib/format';
import { FormGrid, FormPage } from '../components/FormPage';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingBlock,
  PageHeader,
  useToast,
} from '../components/ui';

/**
 * The chat's sticker packs (operator, 2026-10-20: "in chat system i want to add
 * stickers to chats"; asked where they come from, "Company packs only"). The
 * administrators' page (`chat.stickers.manage`): a card a pack, in the order the
 * chat shows them, each with its stickers. Pictures go up the moment they are
 * picked; a pack's name is a form of its own (`/sticker-packs/new`, `…/:id/edit`).
 *
 * A sticker that was sent is never deleted for good — it leaves its pack and
 * stays on its messages; one never sent goes, picture and all. The API decides.
 */

interface AdminSticker {
  id: string;
  label: string;
  url: string;
  mimeType: string;
  sizeBytes: number;
  /** How many messages it was sent on. */
  sent: number;
  createdAt: string;
}

interface AdminStickerPack {
  id: string;
  name: string;
  nameAr: string | null;
  isActive: boolean;
  sortOrder: number;
  stickers: AdminSticker[];
  sent: number;
}

interface StickerPacks {
  items: AdminStickerPack[];
  limits: { maxPacks: number; maxStickers: number; maxBytes: number; types: string[] };
}

const packsKey = ['chat', 'sticker-packs'] as const;
const packKey = (id: string) => ['chat', 'sticker-packs', id] as const;

/** The longest side a still picture is kept at: sharp at the chat's 160 px on any screen. */
const SIDE = 512;
/** Pictures that may move: kept as they come, never redrawn — a canvas keeps only the first frame. */
const MOVING = new Set(['image/gif', 'image/webp']);
/** A post brings this many files at most (the API's MAX_FILES_PER_POST). */
const PER_POST = 20;

type Prepared = { blob: Blob; name: string } | { problem: 'notPicture' | 'tooLarge' | 'unreadable' };

const baseName = (name: string) => name.replace(/\.[^.]+$/, '');

/**
 * A picture as its sticker is uploaded: a GIF or WebP as it is; a PNG or JPEG
 * larger than 512 px, or than the limit, redrawn as a PNG no larger than that —
 * a transparent background stays transparent.
 */
async function prepare(file: File, types: string[], maxBytes: number): Promise<Prepared> {
  const type = file.type.toLowerCase();
  if (!types.includes(type)) return { problem: 'notPicture' };
  if (MOVING.has(type)) return file.size > maxBytes ? { problem: 'tooLarge' } : { blob: file, name: file.name };

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return { problem: 'unreadable' };
  }
  try {
    if (bitmap.width <= SIDE && bitmap.height <= SIDE && file.size <= maxBytes) return { blob: file, name: file.name };
    const scale = Math.min(1, SIDE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext('2d');
    if (!context) return { problem: 'unreadable' };
    context.imageSmoothingQuality = 'high';
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const png = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!png) return { problem: 'unreadable' };
    if (png.size > maxBytes) return { problem: 'tooLarge' };
    return { blob: png, name: `${baseName(file.name) || 'sticker'}.png` };
  } finally {
    bitmap.close();
  }
}

// ---------------------------------------------------------------------------
// The packs
// ---------------------------------------------------------------------------

/** Route `/sticker-packs`. */
export function ChatStickersPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const qc = useQueryClient();
  const packs = useQuery({ queryKey: packsKey, queryFn: () => api.get<StickerPacks>('/chat/sticker-packs') });
  const items = packs.data?.items ?? [];
  const limits = packs.data?.limits;
  const full = !!limits && items.length >= limits.maxPacks;

  /** What changed here changes the chat's tray too. */
  const refresh = (data?: StickerPacks) => {
    if (data) qc.setQueryData(packsKey, data);
    else void qc.invalidateQueries({ queryKey: packsKey });
    void qc.invalidateQueries({ queryKey: chatKeys.stickers });
  };

  const reorder = useMutation({
    mutationFn: (packIds: string[]) => api.put<StickerPacks>('/chat/sticker-packs/order', { packIds }),
    onSuccess: (data) => {
      refresh(data);
      toast.success(t('stickers.orderSaved'));
    },
    onError: (e: Error) => {
      toast.error(e.message);
      refresh();
    },
  });
  const move = (index: number, by: -1 | 1) => {
    const ids = items.map((p) => p.id);
    [ids[index], ids[index + by]] = [ids[index + by], ids[index]];
    reorder.mutate(ids);
  };

  // Back from making a pack: to its card, where its stickers are added.
  useEffect(() => {
    if (!location.hash || !packs.data) return;
    document.getElementById(location.hash.slice(1))?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [location.hash, packs.data]);

  const newPack = (
    <Button
      variant="primary"
      icon={<Plus className="h-4 w-4" />}
      disabled={full}
      title={full && limits ? t('stickers.packsMax', { max: limits.maxPacks }) : undefined}
      onClick={() => navigate('/sticker-packs/new')}
    >
      {t('stickers.newPack')}
    </Button>
  );

  return (
    <div className="max-w-5xl">
      <PageHeader
        title={t('stickers.title')}
        subtitle={t('stickers.subtitle')}
        count={packs.data ? items.length : undefined}
        action={newPack}
      />
      {packs.isLoading ? (
        <LoadingBlock />
      ) : packs.error ? (
        <ErrorState error={packs.error} onRetry={() => void packs.refetch()} />
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            title={t('stickers.empty')}
            hint={t('stickers.emptyHint')}
            icon={<Sticker className="h-8 w-8" />}
            action={newPack}
          />
        </Card>
      ) : (
        <div className="space-y-5">
          {full && limits && (
            <p className="text-body-sm text-muted">{t('stickers.packsMax', { max: formatNumber(limits.maxPacks, locale) })}</p>
          )}
          {items.map((pack, index) => (
            <PackCard
              key={pack.id}
              pack={pack}
              limits={limits!}
              onUp={index > 0 ? () => move(index, -1) : null}
              onDown={index < items.length - 1 ? () => move(index, 1) : null}
              moving={reorder.isPending}
              onChanged={refresh}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PackCard({
  pack,
  limits,
  onUp,
  onDown,
  moving,
  onChanged,
}: {
  pack: AdminStickerPack;
  limits: StickerPacks['limits'];
  onUp: (() => void) | null;
  onDown: (() => void) | null;
  moving: boolean;
  onChanged: () => void;
}) {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [adding, setAdding] = useState<{ done: number; total: number } | null>(null);
  const [deleting, setDeleting] = useState<AdminSticker | null>(null);
  const [deletingPack, setDeletingPack] = useState(false);
  const name = localized(pack);
  // The other language's name, when it has one of its own.
  const otherName = locale === 'ar' ? pack.name : pack.nameAr;
  const room = limits.maxStickers - pack.stickers.length;
  const mb = formatNumber(limits.maxBytes / 1024 / 1024, locale);

  const visibility = useMutation({
    mutationFn: () => api.patch(`/chat/sticker-packs/${pack.id}`, { isActive: !pack.isActive }),
    onSuccess: () => {
      toast.success(pack.isActive ? t('stickers.packHidden', { name }) : t('stickers.packShown', { name }));
      onChanged();
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const removeSticker = useMutation({
    mutationFn: (sticker: AdminSticker) => api.del(`/chat/stickers/${sticker.id}`),
    onSuccess: () => {
      toast.success(t('stickers.deleted'));
      setDeleting(null);
      onChanged();
    },
    onError: (e: Error) => {
      toast.error(e.message);
      setDeleting(null);
    },
  });
  const removePack = useMutation({
    mutationFn: () => api.del(`/chat/sticker-packs/${pack.id}`),
    onSuccess: () => {
      toast.success(t('stickers.packDeleted'));
      setDeletingPack(false);
      onChanged();
    },
    onError: (e: Error) => {
      toast.error(e.message);
      setDeletingPack(false);
    },
  });

  const problemText = (problem: 'notPicture' | 'tooLarge' | 'unreadable', fileName: string) =>
    problem === 'notPicture'
      ? t('stickers.notPicture', { name: fileName })
      : problem === 'tooLarge'
        ? t('stickers.tooLarge', { name: fileName, mb })
        : t('stickers.unreadable', { name: fileName });

  /** Each picture prepared and uploaded in turn, then all added at once — up to the pack's room. */
  const add = async (list: FileList) => {
    if (room <= 0) {
      toast.error(t('stickers.full', { max: limits.maxStickers }));
      return;
    }
    const files = Array.from(list);
    const picked = files.slice(0, room);
    if (files.length > picked.length) toast.toast(t('stickers.room', { n: formatNumber(room, locale) }));
    setAdding({ done: 0, total: picked.length });
    const fileIds: string[] = [];
    try {
      for (const [i, file] of picked.entries()) {
        const prepared = await prepare(file, limits.types, limits.maxBytes);
        if ('problem' in prepared) {
          toast.error(problemText(prepared.problem, file.name));
        } else {
          try {
            fileIds.push((await uploadFile(prepared.blob, prepared.name)).id);
          } catch (e) {
            const storage =
              e instanceof ApiError &&
              ['FILE_STORAGE_UNREACHABLE', 'FILE_STORAGE_REFUSED', 'FILE_STORAGE_UNAVAILABLE'].includes(e.code);
            toast.error(storage ? t('stickers.storage') : `${file.name}: ${e instanceof Error ? e.message : String(e)}`);
          }
        }
        setAdding({ done: i + 1, total: picked.length });
      }
      for (let start = 0; start < fileIds.length; start += PER_POST) {
        await api.post(`/chat/sticker-packs/${pack.id}/stickers`, { fileIds: fileIds.slice(start, start + PER_POST) });
      }
      if (fileIds.length > 0) {
        toast.success(
          fileIds.length === 1 ? t('stickers.addedOne') : t('stickers.addedMany', { n: formatNumber(fileIds.length, locale) }),
        );
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setAdding(null);
      onChanged();
    }
  };

  const sentText = (n: number) =>
    n === 0 ? t('stickers.neverSent') : n === 1 ? t('stickers.sentOnce') : t('stickers.sentMany', { n: formatNumber(n, locale) });

  return (
    <section id={`pack-${pack.id}`} aria-labelledby={`pack-${pack.id}-title`} className="scroll-mt-20">
      <Card padded={false}>
        <div className="flex flex-wrap items-start gap-x-4 gap-y-3 border-b hairline px-5 py-4">
          <div className="min-w-0 flex-1 basis-56">
            <h2 id={`pack-${pack.id}-title`} className="flex flex-wrap items-center gap-2 text-h3 text-[var(--text-primary)]">
              <bdi className="min-w-0 break-words">{name}</bdi>
              {!pack.isActive && <Badge tone="neutral">{t('stickers.hidden')}</Badge>}
            </h2>
            <p className="mt-0.5 text-body-sm text-muted">
              {otherName && otherName !== name && (
                <>
                  <bdi dir={locale === 'ar' ? 'ltr' : 'rtl'}>{otherName}</bdi>
                  <span aria-hidden> · </span>
                </>
              )}
              <span className="tabular">
                {t('stickers.count', {
                  n: formatNumber(pack.stickers.length, locale),
                  max: formatNumber(limits.maxStickers, locale),
                })}
              </span>
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <Button
              size="sm"
              variant="ghost"
              aria-label={t('stickers.moveUp')}
              title={t('stickers.moveUp')}
              disabled={!onUp || moving}
              onClick={() => onUp?.()}
              className="px-2"
            >
              <ArrowUp className="h-4 w-4" aria-hidden />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              aria-label={t('stickers.moveDown')}
              title={t('stickers.moveDown')}
              disabled={!onDown || moving}
              onClick={() => onDown?.()}
              className="px-2"
            >
              <ArrowDown className="h-4 w-4" aria-hidden />
            </Button>
            <Button
              size="sm"
              loading={visibility.isPending}
              icon={pack.isActive ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              onClick={() => visibility.mutate()}
            >
              {pack.isActive ? t('stickers.hide') : t('stickers.show')}
            </Button>
            <Button size="sm" icon={<Pencil className="h-4 w-4" />} onClick={() => navigate(`/sticker-packs/${pack.id}/edit`)}>
              {t('common.edit')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              aria-label={t('stickers.deletePack')}
              title={t('stickers.deletePack')}
              onClick={() => setDeletingPack(true)}
              className="px-2 text-error-fg hover:text-error-fg dark:text-error-dark dark:hover:text-error-dark"
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </div>

        <div className="px-5 py-4">
          {pack.stickers.length === 0 && <p className="mb-3 text-body-sm text-muted">{t('stickers.emptyPack')}</p>}
          <ul className="grid grid-cols-[repeat(auto-fill,minmax(7rem,1fr))] gap-3">
            {pack.stickers.map((sticker) => (
              <li key={sticker.id} className="group relative rounded-md border hairline bg-[var(--surface-sunken)] p-2">
                <img
                  src={sticker.url}
                  alt=""
                  loading="lazy"
                  draggable={false}
                  className="mx-auto block h-20 w-20 object-contain"
                />
                <p className="mt-1.5 truncate text-center text-caption font-medium text-[var(--text-primary)]" title={sticker.label}>
                  {sticker.label}
                </p>
                <p className="truncate text-center text-[11px] text-[var(--text-tertiary)]">{sentText(sticker.sent)}</p>
                <button
                  type="button"
                  onClick={() => setDeleting(sticker)}
                  aria-label={t('stickers.delete', { label: sticker.label })}
                  title={t('stickers.delete', { label: sticker.label })}
                  className="absolute end-1 top-1 grid h-7 w-7 place-items-center rounded-full bg-[var(--surface-card)] text-[var(--text-secondary)] opacity-0 shadow-sm ring-1 ring-black/10 transition-opacity duration-fast hover:text-error-fg focus-visible:opacity-100 group-hover:opacity-100 dark:ring-white/10 dark:hover:text-error-dark [@media(hover:none)]:opacity-100"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </li>
            ))}
            <li>
              <button
                type="button"
                onClick={() => input.current?.click()}
                disabled={!!adding || room <= 0}
                title={room <= 0 ? t('stickers.full', { max: limits.maxStickers }) : undefined}
                className={clsx(
                  'flex h-full min-h-[8.5rem] w-full flex-col items-center justify-center gap-1.5 rounded-md border-2 border-dashed px-2 py-3 text-center text-caption font-medium transition-colors duration-fast',
                  'border-[var(--line-strong)] text-[var(--text-secondary)] hover:border-primary/60 hover:text-primary-600 dark:hover:text-primary-400',
                  'disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-[var(--line-strong)] disabled:hover:text-[var(--text-secondary)]',
                )}
              >
                {adding ? (
                  <>
                    <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
                    <span>{t('stickers.uploading')}</span>
                    <span className="tabular" dir="ltr">
                      {formatNumber(adding.done, locale)}/{formatNumber(adding.total, locale)}
                    </span>
                  </>
                ) : (
                  <>
                    <ImagePlus className="h-6 w-6" aria-hidden />
                    <span>{t('stickers.add')}</span>
                  </>
                )}
              </button>
              <input
                ref={input}
                type="file"
                multiple
                hidden
                accept={limits.types.join(',')}
                onChange={(e) => {
                  if (e.target.files?.length) void add(e.target.files);
                  e.target.value = '';
                }}
              />
            </li>
          </ul>
          <p className="mt-3 text-caption text-faint">{t('stickers.addHint', { mb })}</p>
        </div>
      </Card>

      {deleting && (
        <ConfirmDialog
          title={t('stickers.deleteConfirm')}
          confirmLabel={t('common.delete')}
          danger
          busy={removeSticker.isPending}
          onConfirm={() => removeSticker.mutate(deleting)}
          onCancel={() => setDeleting(null)}
        >
          <div className="flex items-center gap-3">
            <img src={deleting.url} alt="" className="h-16 w-16 shrink-0 object-contain" />
            <p>{deleting.sent > 0 ? t('stickers.deleteBodySent') : t('stickers.deleteBodyUnsent')}</p>
          </div>
        </ConfirmDialog>
      )}
      {deletingPack && (
        <ConfirmDialog
          title={t('stickers.deletePackConfirm', { name })}
          confirmLabel={t('stickers.deletePack')}
          danger
          busy={removePack.isPending}
          onConfirm={() => removePack.mutate()}
          onCancel={() => setDeletingPack(false)}
        >
          <p>{t('stickers.deletePackBody')}</p>
        </ConfirmDialog>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// A pack's name
// ---------------------------------------------------------------------------

/** The API's own limit on a pack's name, in each language. */
const NAME_MAX = 40;

/** Routes `/sticker-packs/new` and `/sticker-packs/:id/edit`. */
export function StickerPackFormPage() {
  const { id } = useParams();
  const editing = !!id;
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const back = '/sticker-packs';

  const existing = useQuery({
    queryKey: packKey(id ?? ''),
    queryFn: () => api.get<AdminStickerPack>(`/chat/sticker-packs/${id}`),
    enabled: editing,
  });
  const [form, setForm] = useState({ name: '', nameAr: '', isActive: true });
  const [loaded, setLoaded] = useState(false);
  if (editing && existing.data && !loaded) {
    setLoaded(true);
    setForm({ name: existing.data.name, nameAr: existing.data.nameAr ?? '', isActive: existing.data.isActive });
  }

  const save = useMutation({
    mutationFn: () => {
      const body = { name: form.name.trim(), nameAr: form.nameAr.trim() || null, isActive: form.isActive };
      return editing
        ? api.patch<AdminStickerPack>(`/chat/sticker-packs/${id}`, body)
        : api.post<AdminStickerPack>('/chat/sticker-packs', body);
    },
    onSuccess: (pack) => {
      toast.success(editing ? t('stickers.packSaved') : t('stickers.packCreated'));
      void qc.invalidateQueries({ queryKey: packsKey });
      void qc.invalidateQueries({ queryKey: chatKeys.stickers });
      navigate(`${back}#pack-${pack.id}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const valid = !!form.name.trim();

  return (
    <FormPage
      title={editing ? t('stickers.editPack') : t('stickers.newPack')}
      subtitle={t('stickers.formNote')}
      backTo={back}
      backLabel={t('stickers.title')}
      submitLabel={t('common.save')}
      submitting={save.isPending}
      disabled={!valid}
      loading={editing && existing.isLoading}
      error={existing.error}
      onRetry={() => void existing.refetch()}
      onSubmit={() => save.mutate()}
    >
      <FormGrid>
        <Field label={t('stickers.packName')} required>
          <Input
            value={form.name}
            maxLength={NAME_MAX}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            autoFocus
          />
        </Field>
        <Field label={t('stickers.packNameAr')} hint={t('stickers.packNameArHint')}>
          <Input dir="rtl" value={form.nameAr} maxLength={NAME_MAX} onChange={(e) => setForm({ ...form, nameAr: e.target.value })} />
        </Field>
        <Field label={t('stickers.inChat')} hint={t('stickers.shownHint')} className="sm:col-span-2">
          <Checkbox
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            label={t('stickers.shown')}
          />
        </Field>
      </FormGrid>
    </FormPage>
  );
}
